import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';
import { usePreferenceScope } from '@/hooks/useStoredPreference';
import { useDebouncedSearch } from '@/hooks/useServerList';
import type { Client } from '@/pages/Tenders/types';
import type { OpportunityFormValues } from '@/pages/Opportunities/formSchema';

export interface OpportunityClientFieldProps { form: UseFormReturn<OpportunityFormValues>; selected?: Client; }
export const OpportunityClientField = ({ form, selected }: OpportunityClientFieldProps) => {
    const { t } = useTranslation('global');
    const [search, setSearch] = useState('');
    const delayed = useDebouncedSearch(search);
    const scope = usePreferenceScope();
    const clients = useQuery({ queryKey: ['opportunity-clients', scope, delayed], queryFn: async ({ signal }) =>
        (await api.get<{ data: Client[] }>('/opportunities/clients', { params: { search: delayed }, signal })).data.data });
    const options = [...(selected ? [selected] : []), ...(clients.data ?? []).filter(item => item.id !== selected?.id)];
    const client = options.find(item => String(item.id) === form.watch('client_id'));
    return <section className="space-y-3">
        <Select label={t('opportunities.fields.client_id')} required searchable value={form.watch('client_id')} onSearchChange={setSearch}
            onChange={(value: string) => form.setValue('client_id', String(value), { shouldDirty: true })} options={options.map(item => ({ value: String(item.id), label: item.name }))} error={form.formState.errors.client_id?.message} />
        {client && <><Input label={t('opportunities.tax_id')} value={client.tax_id ?? ''} readOnly /><Input label={t('opportunities.phone')} value={client.phone ?? ''} readOnly /><Input label={t('opportunities.email')} value={client.email ?? ''} readOnly /></>}
        <Button variant="outline" icon="externalLink" onClick={() => window.open('/clients', '_blank', 'noopener,noreferrer')}>{t('opportunities.manage_clients')}</Button>
        <Button variant="ghost" icon="refresh" onClick={() => { void clients.refetch(); }}>{t('opportunities.refresh_clients')}</Button>
        {clients.isError && <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">{t('opportunities.load_error')}</p>}
    </section>;
};
